import { ReactNode } from 'react';

import { cn } from '@/lib/utils';
import { PageContainer } from '../../../common/components/PageContainer';

export interface AdminPageProps {
  /** The page's name: heading, and the last breadcrumb. */
  title: string;
  /** One line under the title saying what the page is for. */
  description?: ReactNode;
  /** Buttons at the heading's end (primary action last). */
  actions?: ReactNode;
  /** Breadcrumb trail before the title, e.g. [{ label: 'Vendors', href: '/vendors' }]. */
  parents?: { label: string; href?: string }[];
  /** Shown at the top bar's end (e.g. a link to a related page). */
  status?: ReactNode;
  /** Heading row replacement for detail pages (logo, badges…). */
  header?: ReactNode;
  className?: string;
  children: ReactNode;
}

/**
 * Every super-admin page's frame: the top bar with breadcrumbs, then a heading
 * with what the page is for and its actions, then the page. One layout so the
 * portal reads as one product, not a page per author.
 */
export function AdminPage({ title, description, actions, parents = [], status, header, className, children }: AdminPageProps) {
  return (
    <PageContainer breadcrumbs={[...parents, { label: title }]} status={status}>
      <div className={cn('flex flex-col gap-6', className)}>
        {header ?? (
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div className="min-w-0">
              <h1 className="font-heading text-2xl font-medium tracking-tight">{title}</h1>
              {description && <p className="mt-1 max-w-2xl text-sm text-muted-foreground">{description}</p>}
            </div>
            {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
          </div>
        )}
        {children}
      </div>
    </PageContainer>
  );
}
